<template>
  <div>
    <Head title="Detalles de Devolución" />
    
    <!-- Encabezado -->
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
      <div class="flex items-center">
        <Link href="/devolutions" class="mr-4 p-2 text-white bg-gray-700 hover:bg-gray-800 rounded transition-colors" title="Volver a la lista">
          <i class="fa fa-arrow-left"></i>
        </Link>
        <div>
          <div class="flex items-center space-x-3">
            <h1 class="text-3xl font-bold text-gray-800">Devolución #DEV-{{ padZero(devolution.id) }}</h1>
            <span :class="getStatusBadgeClass(devolution.status)" class="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
              {{ getStatusText(devolution.status) }}
            </span>
          </div>
          <p class="text-sm text-gray-500 mt-1">Registrada el {{ formatDateTime(devolution.created_at) }}</p>
        </div>
      </div>

      <div class="flex items-center space-x-3">
        <!-- BOTÓN IMPRIMIR PDF -->
        <a
          :href="`/devolutions/${devolution.id}/pdf`"
          target="_blank"
          class="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md font-semibold text-sm shadow flex items-center space-x-2 transition-colors"
        >
          <i class="fa fa-print"></i>
          <span>Imprimir Soporte PDF</span>
        </a>

        <!-- BOTÓN APROBAR SI ESTÁ PENDIENTE -->
        <button
          v-if="devolution.status === 'pending'"
          @click="approveDevolution"
          class="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md font-semibold text-sm shadow flex items-center space-x-2 transition-colors"
        >
          <i class="fa fa-check"></i>
          <span>Aprobar Devolución</span>
        </button>
      </div>
    </div>

    <!-- TARJETAS DE INFORMACIÓN GENERAL -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <!-- DATOS DE LA FACTURA DE ORIGEN -->
      <div class="bg-white rounded-lg shadow p-5 border border-gray-100">
        <div class="flex items-center justify-between mb-3">
          <span class="text-xs font-bold uppercase text-gray-500">Factura / Venta Origen</span>
          <Link :href="`/orders/${devolution.order_id}`" class="text-xs text-blue-600 hover:underline font-semibold flex items-center">
            Ver Factura <i class="fa fa-external-link-alt ml-1 text-[10px]"></i>
          </Link>
        </div>
        <div class="text-lg font-bold text-gray-900">
          #{{ devolution.order.invoice_number || devolution.order.number_order }}
        </div>
        <div class="text-xs text-gray-500 mt-1">
          Fecha: {{ formatDate(devolution.order.created_at) }}
        </div>
        <div class="mt-3 pt-3 border-t border-gray-100 flex justify-between text-xs">
          <span class="text-gray-500">Total Factura:</span>
          <span class="font-bold font-mono text-gray-800">${{ formatCurrency(devolution.order.total) }}</span>
        </div>
        <div class="flex justify-between text-xs mt-1">
          <span class="text-gray-500">Saldo Factura:</span>
          <span class="font-bold font-mono text-gray-800">${{ formatCurrency(devolution.order.balance) }}</span>
        </div>
      </div>

      <!-- DATOS DEL CLIENTE -->
      <div class="bg-white rounded-lg shadow p-5 border border-gray-100">
        <span class="text-xs font-bold uppercase text-gray-500 block mb-3">Información del Cliente</span>
        <div class="text-lg font-bold text-gray-900">
          {{ devolution.order.client ? devolution.order.client.name : 'Cliente no disponible' }}
        </div>
        <div class="text-xs text-gray-500 mt-1">
          RIF/CI: {{ devolution.order.client ? devolution.order.client.identification : '-' }}
        </div>
        <div class="text-xs text-gray-500">
          Tel: {{ devolution.order.client ? (devolution.order.client.phone || '-') : '-' }}
        </div>
        <div class="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500 truncate">
          Dir: {{ devolution.order.client ? (devolution.order.client.address || '-') : '-' }}
        </div>
      </div>

      <!-- RESOLUCIÓN Y TOTAL -->
      <div class="bg-white rounded-lg shadow p-5 border border-gray-100 flex flex-col justify-between">
        <div>
          <span class="text-xs font-bold uppercase text-gray-500 block mb-1">Modalidad de Resolución</span>
          <div class="flex items-center space-x-2">
            <span :class="getResolutionBadgeClass(devolution.resolution_type)" class="px-2.5 py-1 rounded text-xs font-bold uppercase">
              {{ getResolutionText(devolution.resolution_type) }}
            </span>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t border-gray-100">
          <span class="text-xs text-gray-500 uppercase block">Total Devuelto</span>
          <div class="text-2xl font-black font-mono text-blue-600">
            ${{ formatCurrency(devolution.total_amount) }}
          </div>
        </div>
      </div>
    </div>

    <!-- 1. TABLA DE PRODUCTOS DEVUELTOS -->
    <div class="bg-white rounded-lg shadow mb-6 border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <h3 class="font-bold text-gray-800 text-base flex items-center space-x-2">
          <i class="fa fa-undo text-blue-600"></i>
          <span>Productos Devueltos</span>
        </h3>
        <span class="text-xs text-gray-500 font-medium">
          {{ devolution.devolution_products.length }} producto(s)
        </span>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="bg-gray-50 text-gray-700 text-xs uppercase font-semibold">
            <tr>
              <th class="p-4">Código</th>
              <th class="p-4">Producto</th>
              <th class="p-4 text-center">Cant. Devuelta</th>
              <th class="p-4 text-right">Precio Unit.</th>
              <th class="p-4 text-right">Subtotal</th>
              <th class="p-4">Motivo</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="item in devolution.devolution_products" :key="item.id" class="hover:bg-gray-50">
              <td class="p-4 font-mono text-xs text-gray-600">
                {{ item.order_product && item.order_product.product ? item.order_product.product.code : '-' }}
              </td>
              <td class="p-4 font-medium text-gray-900">
                {{ item.order_product && item.order_product.product ? item.order_product.product.name : 'Producto #' + item.product_id }}
              </td>
              <td class="p-4 text-center font-bold text-gray-800">{{ item.quantity }}</td>
              <td class="p-4 text-right font-mono text-gray-600">
                ${{ formatCurrency(item.amount / Math.max(1, item.quantity)) }}
              </td>
              <td class="p-4 text-right font-mono font-bold text-gray-900">
                ${{ formatCurrency(item.amount) }}
              </td>
              <td class="p-4 text-xs text-gray-500 italic">
                {{ item.reason || 'Sin motivo especificado' }}
              </td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 border-t font-bold">
            <tr>
              <td colspan="4" class="p-4 text-right uppercase text-xs text-gray-600">Total Valor Devuelto:</td>
              <td class="p-4 text-right font-mono text-base text-blue-700">
                ${{ formatCurrency(devolution.total_amount) }}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <!-- 2. DETALLE DE LA RESOLUCIÓN -->
    <!-- CASO 1: REEMBOLSO -->
    <div v-if="devolution.resolution_type === 'refund'" class="bg-white rounded-lg shadow mb-6 border border-gray-100 p-6">
      <h3 class="font-bold text-gray-800 text-base mb-4 flex items-center space-x-2">
        <i class="fa fa-money-bill-wave text-green-600"></i>
        <span>Detalles del Reembolso de Dinero</span>
      </h3>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 bg-green-50/50 p-4 rounded-lg border border-green-100 text-sm">
        <div>
          <span class="text-xs text-gray-500 uppercase font-semibold block">Método de Reembolso</span>
          <span class="text-gray-800 font-bold">{{ devolution.refund_method || 'Efectivo' }}</span>
        </div>
        <div>
          <span class="text-xs text-gray-500 uppercase font-semibold block">Referencia</span>
          <span class="text-gray-800 font-medium font-mono">{{ devolution.refund_reference || 'N/A' }}</span>
        </div>
        <div>
          <span class="text-xs text-gray-500 uppercase font-semibold block">Monto Reembolsado</span>
          <span class="text-lg font-bold font-mono text-green-700">${{ formatCurrency(devolution.total_amount) }}</span>
        </div>
      </div>
    </div>

    <!-- CASO 2: CAMBIO DE PRODUCTO -->
    <div v-if="devolution.resolution_type === 'exchange'" class="bg-white rounded-lg shadow mb-6 border border-gray-100 overflow-hidden">
      <div class="px-6 py-4 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
        <h3 class="font-bold text-gray-800 text-base flex items-center space-x-2">
          <i class="fa fa-boxes-stacked text-indigo-600"></i>
          <span>Productos Entregados a Cambio</span>
        </h3>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full text-left text-sm">
          <thead class="bg-gray-50 text-gray-700 text-xs uppercase font-semibold">
            <tr>
              <th class="p-4">Código</th>
              <th class="p-4">Producto</th>
              <th class="p-4 text-center">Cant. Entregada</th>
              <th class="p-4 text-right">Precio Unit.</th>
              <th class="p-4 text-right">Total</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="item in devolution.devolution_exchange_products" :key="item.id" class="hover:bg-gray-50">
              <td class="p-4 font-mono text-xs text-gray-600">{{ item.product ? item.product.code : '-' }}</td>
              <td class="p-4 font-medium text-gray-900">{{ item.product ? item.product.name : 'Producto #' + item.product_id }}</td>
              <td class="p-4 text-center font-bold">{{ item.quantity }}</td>
              <td class="p-4 text-right font-mono">${{ formatCurrency(item.price) }}</td>
              <td class="p-4 text-right font-mono font-bold text-gray-900">${{ formatCurrency(item.amount) }}</td>
            </tr>
            <tr v-if="!devolution.devolution_exchange_products || !devolution.devolution_exchange_products.length">
              <td colspan="5" class="p-4 text-center text-gray-400 text-xs">No se registraron productos a cambio.</td>
            </tr>
          </tbody>
          <tfoot class="bg-gray-50 border-t font-bold">
            <tr>
              <td colspan="4" class="p-4 text-right uppercase text-xs text-gray-600">Total Productos Entregados:</td>
              <td class="p-4 text-right font-mono text-base text-indigo-700">
                ${{ formatCurrency(devolution.exchange_amount) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- BALANCE DE LA DIFERENCIA -->
      <div class="p-6 bg-gray-50 border-t border-gray-100">
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 items-center">
          <div>
            <span class="text-xs text-gray-500 uppercase block">Valor Devuelto</span>
            <span class="text-base font-bold font-mono text-gray-800">${{ formatCurrency(devolution.total_amount) }}</span>
          </div>
          <div>
            <span class="text-xs text-gray-500 uppercase block">Valor Entregado</span>
            <span class="text-base font-bold font-mono text-indigo-700">${{ formatCurrency(devolution.exchange_amount) }}</span>
          </div>
          <div class="p-3 rounded-lg border" :class="devolution.difference_amount > 0 ? 'bg-red-50 border-red-200' : (devolution.difference_amount < 0 ? 'bg-green-50 border-green-200' : 'bg-gray-100 border-gray-200')">
            <span class="text-xs font-bold uppercase block" :class="devolution.difference_amount > 0 ? 'text-red-700' : (devolution.difference_amount < 0 ? 'text-green-700' : 'text-gray-700')">
              {{ devolution.difference_amount > 0 ? 'Diferencia Cobrada al Cliente' : (devolution.difference_amount < 0 ? 'Diferencia a Favor del Cliente' : 'Sin Diferencia (Equivalente)') }}
            </span>
            <span class="text-lg font-bold font-mono" :class="devolution.difference_amount > 0 ? 'text-red-700' : (devolution.difference_amount < 0 ? 'text-green-700' : 'text-gray-700')">
              ${{ formatCurrency(Math.abs(devolution.difference_amount)) }}
            </span>
            <div v-if="devolution.difference_payment_method" class="text-xs text-gray-500 mt-1">
              Método: {{ devolution.difference_payment_method }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- CASO 3: FONDO PARA VENTA MAYOR / ABONO A OTRA FACTURA -->
    <div v-if="devolution.resolution_type === 'store_credit'" class="bg-white rounded-lg shadow mb-6 border border-gray-100 p-6">
      <h3 class="font-bold text-gray-800 text-base mb-4 flex items-center space-x-2">
        <i class="fa fa-file-invoice-dollar text-purple-600"></i>
        <span>Fondo para Venta Mayor / Abono a Factura</span>
      </h3>

      <div v-if="devolution.target_order" class="bg-purple-50 p-5 rounded-lg border border-purple-200">
        <div class="flex items-center justify-between mb-3">
          <div>
            <span class="text-xs font-bold uppercase text-purple-700">Factura Beneficiada con el Abono</span>
            <h4 class="text-lg font-bold text-purple-900">
              Factura #{{ devolution.target_order.invoice_number || devolution.target_order.number_order }}
            </h4>
          </div>
          <Link :href="`/orders/${devolution.target_order.id}`" class="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white rounded text-xs font-semibold flex items-center space-x-1">
            <span>Ver Factura Destino</span>
            <i class="fa fa-external-link-alt text-[10px]"></i>
          </Link>
        </div>

        <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs pt-3 border-t border-purple-200">
          <div>
            <span class="text-gray-500 block">Total Factura Destino:</span>
            <span class="font-bold font-mono text-gray-800 text-sm">${{ formatCurrency(devolution.target_order.total) }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Monto Abonado por Devolución:</span>
            <span class="font-bold font-mono text-purple-700 text-sm">+${{ formatCurrency(devolution.total_amount) }}</span>
          </div>
          <div>
            <span class="text-gray-500 block">Saldo Restante Actual:</span>
            <span class="font-bold font-mono text-red-600 text-sm">
              ${{ formatCurrency(Math.max(0, devolution.target_order.total - devolution.target_order.balance)) }}
            </span>
          </div>
          <div>
            <span class="text-gray-500 block">Estado Actual:</span>
            <span class="font-semibold text-gray-800 uppercase">{{ devolution.target_order.order_state }}</span>
          </div>
        </div>
      </div>

      <div v-else class="p-4 bg-gray-50 rounded-lg text-sm text-gray-700 border border-gray-200">
        <i class="fa fa-info-circle text-blue-500 mr-2"></i>
        Este monto de <strong>${{ formatCurrency(devolution.total_amount) }}</strong> fue acreditado como saldo a favor en la cuenta del cliente para futuras compras.
      </div>
    </div>

    <!-- 3. OBSERVACIONES / NOTAS -->
    <div v-if="devolution.notes" class="bg-white rounded-lg shadow mb-6 border border-gray-100 p-6">
      <h4 class="text-xs font-bold uppercase text-gray-500 mb-2">Observaciones / Notas</h4>
      <p class="text-sm text-gray-700 whitespace-pre-line bg-gray-50 p-4 rounded border border-gray-100">
        {{ devolution.notes }}
      </p>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
  },
  layout: Layout,
  props: {
    devolution: Object,
  },
  methods: {
    padZero(num) {
      return String(num).padStart(5, '0')
    },
    formatCurrency(amount) {
      const num = Number(amount) || 0
      return num.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatDate(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleDateString('es-VE')
    },
    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      return new Date(dateStr).toLocaleString('es-VE', { hour12: false })
    },
    getStatusBadgeClass(status) {
      return {
        'approved': 'bg-green-100 text-green-800 border border-green-200',
        'pending': 'bg-yellow-100 text-yellow-800 border border-yellow-200',
        'rejected': 'bg-red-100 text-red-800 border border-red-200'
      }[status] || 'bg-gray-100 text-gray-800'
    },
    getStatusText(status) {
      return {
        'approved': 'Aprobada',
        'pending': 'Pendiente',
        'rejected': 'Rechazada'
      }[status] || status
    },
    getResolutionBadgeClass(type) {
      return {
        'refund': 'bg-green-100 text-green-800 border border-green-200',
        'exchange': 'bg-blue-100 text-blue-800 border border-blue-200',
        'store_credit': 'bg-purple-100 text-purple-800 border border-purple-200'
      }[type] || 'bg-gray-100 text-gray-800'
    },
    getResolutionText(type) {
      return {
        'refund': 'Devolución de Dinero',
        'exchange': 'Cambio de Producto',
        'store_credit': 'Fondo para Venta Mayor / Abono'
      }[type] || type
    },
    approveDevolution() {
      if (confirm('¿Está seguro de aprobar esta devolución? Esta acción actualizará los inventarios y balances.')) {
        this.$inertia.post(`/devolutions/${this.devolution.id}/approve`)
      }
    }
  }
}
</script>
